/*******************************************************************************
 * Copyright (c) 2024-2025 Passport Fintech Inc. All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 ******************************************************************************/

import {Injectable, Renderer2, RendererFactory2, inject} from '@angular/core';
import {Actions, createEffect, ofType, ROOT_EFFECTS_INIT} from '@ngrx/effects';
import {concatLatestFrom} from '@ngrx/operators';
import {catchError, exhaustMap, from, Observable, of, switchMap, take, tap, timer} from 'rxjs';
import {Store} from '@ngrx/store';
import {ThemeActions, TokenActions} from '@shared/common-core/store/actions';
import {PspTheme, PspThemePreloadingClass, THEME_STORAGE_KEYS} from '@shared/models/psp-theme';
import {ThemeSelectors} from '@shared/common-core/store/selectors';
import {AUTH_STORAGE_KEYS} from '@shared/modules/auth/auth-keys';
import {AuthActions} from '@shared/modules/auth/store/actions';
import {LocalStorageService} from '@common-services/local-storage/local-storage.service';

/**
 * This file contains the effects for the app module
 */
@Injectable()
export class ThemeEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private rendererFactory = inject(RendererFactory2);
  private localStorageService = inject(LocalStorageService);

  private renderer: Renderer2;

  constructor() {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  /**
   * This effect is used to handle the application start action
   * If the user is logged in (has token in storage):
   *   - Check if there is a theme stored, and used that one
   * If not, use the default light theme
   */
  init$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ROOT_EFFECTS_INIT),
      exhaustMap(() => {
        const storedSerializedToken: string | null = this.localStorageService.getItem(
          AUTH_STORAGE_KEYS.token
        );

        // If we have a logged in token, we should check the user's theme preference
        if (storedSerializedToken) {
          const storedSerializedTheme: PspTheme | null = this.localStorageService.getItem(
            THEME_STORAGE_KEYS.theme
          ) as PspTheme | null;
          if (storedSerializedTheme) {
            return of(
              ThemeActions.setTheme({theme: storedSerializedTheme, saveToStorage: true})
            );
          }
        }

        return of(ThemeActions.setTheme({theme: 'psp-default', saveToStorage: false}));
      })
    );
  });

  /**
   * This effect is used to handle the fintech/bank signup success action
   * When the user signs up we reset the theme to the default theme
   */
  signUpSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.financialEntitySignUpSuccess, AuthActions.dianSignUpSuccess),
      exhaustMap(() => {
        return of(ThemeActions.setTheme({theme: 'psp-default', saveToStorage: true}));
      })
    );
  });

  /**
   * This effect is used to handle the set token action
   * When the user logs in:
   *   - Check if there is a theme stored, and used that one
   * If not, use the default light theme, and save it as the preferred theme
   */
  setToken$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TokenActions.setToken),
      exhaustMap(() => {
        const storedSerializedTheme: PspTheme | null = this.localStorageService.getItem(
          THEME_STORAGE_KEYS.theme
        ) as PspTheme | null;
        if (storedSerializedTheme) {
          return of(
            ThemeActions.setTheme({theme: storedSerializedTheme, saveToStorage: true})
          );
        }

        return of(ThemeActions.setTheme({theme: 'psp-default', saveToStorage: true}));
      })
    );
  });

  clearTokens$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TokenActions.clearTokens),
      exhaustMap(() => of(ThemeActions.setTheme({theme: 'psp-default', saveToStorage: false})))
    );
  });

  /**
   * This effect is used to handle the setTheme action.
   * It firsts loads the new theme files, and after they are loaded
   * it changes the <html> tag class for our current theme, and removes the old theme class and it's css file
   */
  setTheme$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ThemeActions.setTheme),
      concatLatestFrom(() => this.store.select(ThemeSelectors.selectTheme)),
      tap(() => {
        this.renderer.addClass(document.body, PspThemePreloadingClass);
        document.documentElement.offsetHeight; // Trigger a reflow to flush the CSS changes;
      }),
      exhaustMap(([{theme, saveToStorage}, previousTheme]) => {
        return this.loadThemeFile(`${theme}.css`, theme).pipe(
          switchMap(() => {
            this.renderer.addClass(document.body, theme);
            if (theme !== previousTheme) {
              this.removeUnusedTheme(previousTheme);
            }
            if (saveToStorage) {
              this.localStorageService.setItem(THEME_STORAGE_KEYS.theme, theme);
            }
            document.documentElement.offsetHeight; // Trigger a reflow to flush the CSS changes;
            return of(
              ThemeActions.setThemeSuccess({
                theme: theme,
              })
            );
          }),
          catchError(() => of(ThemeActions.setThemeFailure()))
        );
      })
    );
  });

  /**
   * This effect is used to handle the setTheme action.
   * It firsts loads the new theme files, and after they are loaded
   * it changes the <html> tag class for our current theme, and removes the old theme class and it's css file
   */
  setThemeSuccessOrFail$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ThemeActions.setThemeSuccess, ThemeActions.setThemeFailure),
        switchMap(() => timer(1200).pipe(take(1))),
        tap(() => {
          this.renderer.removeClass(document.body, PspThemePreloadingClass);
        })
      );
    },
    {dispatch: false}
  );

  loadThemeFile(href: string, id: string): Observable<Event> {
    const promise = new Promise<Event>((resolve, reject) => {
      const style = document.createElement('link');
      style.rel = 'stylesheet';
      style.href = href;
      style.id = id;
      style.onload = resolve;
      style.onerror = reject;
      document.head.append(style);
    });
    return from(promise);
  }

  private removeUnusedTheme(theme: PspTheme | null): void {
    this.renderer.removeClass(document.body, theme!);
    const removedThemeStyle = document.getElementById(theme!);
    if (removedThemeStyle) {
      document.head.removeChild(removedThemeStyle);
    }
  }
}
